import {
  EntityModel,
  exportPrisma,
  MeetingParticipantType,
  MeetingType,
} from "client";
import type {
  EventTierAnalyticsDbFilters,
  EventTierMeetingParty,
  EventTierMeetingProfileRow,
  EventTierSessionBookmarkProfileRow,
} from "./types";
import { analyticsCreatedAt, withCreatedAt } from "./prisma-date-range";

const IN_BATCH = 500;
const LIST_LIMIT = 10_000;

type CreatedAtRange = Extract<
  ReturnType<typeof analyticsCreatedAt>,
  { mode: "bounded" | "unbounded" }
>;

function firstName(
  translations: Array<{ name: string }> | undefined
): string | undefined {
  const name = translations?.[0]?.name?.trim();
  return name ? name : undefined;
}

async function mapInBatches<T, R>(
  ids: T[],
  mapper: (batch: T[]) => Promise<R>
): Promise<R[]> {
  const results: R[] = [];
  for (let i = 0; i < ids.length; i += IN_BATCH) {
    results.push(await mapper(ids.slice(i, i + IN_BATCH)));
  }
  return results;
}

function withStartDate<T extends Record<string, unknown>>(
  where: T,
  range: CreatedAtRange
): T {
  if (range.mode === "bounded") {
    return { ...where, startDate: range.createdAt };
  }
  return where;
}

const profileSelect = {
  id: true,
  name: true,
  email: true,
  jobTitle: true,
  company: true,
} as const;

export async function listEventTierSessionBookmarkProfiles(
  eventId: string,
  filters: EventTierAnalyticsDbFilters
): Promise<EventTierSessionBookmarkProfileRow[]> {
  const range = analyticsCreatedAt(filters);
  if (range.mode === "match-none") return [];

  const bookmarks = await exportPrisma.bookmark.findMany({
    where: withCreatedAt(
      { eventId, entityModel: EntityModel.SESSION },
      range
    ),
    take: LIST_LIMIT,
    orderBy: { createdAt: "desc" },
    select: {
      createdAt: true,
      entityId: true,
      userId: true,
      user: {
        select: {
          id: true,
          name: true,
          email: true,
          profiles: {
            where: { eventId },
            take: 1,
            select: profileSelect,
          },
        },
      },
    },
  });

  const sessionIds = [...new Set(bookmarks.map((row) => row.entityId))];
  const sessions = (
    await mapInBatches(sessionIds, (batch) =>
      exportPrisma.sessionV2.findMany({
        where: { id: { in: batch } },
        select: {
          id: true,
          translations: { select: { name: true }, take: 1 },
        },
      })
    )
  ).flat();
  const sessionNameById = new Map(
    sessions.map((session) => [session.id, firstName(session.translations) ?? ""])
  );

  return bookmarks.map((row) => {
    const profile = row.user.profiles[0];
    return {
      profileId: profile?.id ?? "",
      profileName: profile?.name?.trim() || row.user.name?.trim() || "",
      profileEmail: profile?.email?.trim() || row.user.email,
      jobTitle: profile?.jobTitle?.trim() || "",
      company: profile?.company?.trim() || "",
      userId: row.user.id,
      userName: row.user.name?.trim() || "",
      userEmail: row.user.email,
      sessionId: row.entityId,
      sessionName: sessionNameById.get(row.entityId) ?? "",
      bookmarkedAt: row.createdAt.toISOString(),
    };
  });
}

export async function listEventTierMeetingProfiles(
  eventId: string,
  filters: EventTierAnalyticsDbFilters
): Promise<EventTierMeetingProfileRow[]> {
  const range = analyticsCreatedAt(filters);
  if (range.mode === "match-none") return [];

  const meetings = await exportPrisma.meeting.findMany({
    where: withStartDate(
      { eventId, type: { not: MeetingType.SESSION } },
      range
    ),
    take: LIST_LIMIT,
    orderBy: { startDate: "desc" },
    select: {
      id: true,
      status: true,
      type: true,
      startDate: true,
      participants: {
        select: {
          type: true,
          entityType: true,
          entityId: true,
          user: {
            select: {
              id: true,
              name: true,
              email: true,
              profiles: {
                where: { eventId },
                take: 1,
                select: profileSelect,
              },
            },
          },
          exhibitor: {
            select: {
              id: true,
              email: true,
              translations: { select: { name: true }, take: 1 },
            },
          },
          sponsor: {
            select: {
              id: true,
              email: true,
              translations: { select: { name: true }, take: 1 },
            },
          },
        },
      },
    },
  });

  return meetings.map((meeting) => {
    const sender = meeting.participants.find(
      (participant) => participant.type === MeetingParticipantType.SENDER
    );
    const receiver = meeting.participants.find(
      (participant) => participant.type === MeetingParticipantType.RECEIVER
    );

    return {
      meetingId: meeting.id,
      requestStatus: meeting.status ?? "",
      meetingType: meeting.type,
      startDate: meeting.startDate.toISOString(),
      sender: meetingParty(sender),
      receiver: meetingParty(receiver),
    };
  });
}

function meetingParty(
  participant:
    | {
        entityType: string;
        entityId: string;
        user?: {
          id: string;
          name: string | null;
          email: string;
          profiles: Array<{
            id: string;
            name: string | null;
            email: string | null;
            company: string | null;
          }>;
        } | null;
        exhibitor?: {
          id: string;
          email: string | null;
          translations: Array<{ name: string }>;
        } | null;
        sponsor?: {
          id: string;
          email: string | null;
          translations: Array<{ name: string }>;
        } | null;
      }
    | undefined
): EventTierMeetingParty {
  if (!participant) {
    return { id: "", name: "", email: "", type: "", company: "" };
  }

  const profile = participant.user?.profiles[0];
  const exhibitorName = firstName(participant.exhibitor?.translations);
  const sponsorName = firstName(participant.sponsor?.translations);

  return {
    id:
      profile?.id ||
      participant.user?.id ||
      participant.exhibitor?.id ||
      participant.sponsor?.id ||
      participant.entityId,
    name:
      profile?.name?.trim() ||
      participant.user?.name?.trim() ||
      exhibitorName ||
      sponsorName ||
      "",
    email:
      profile?.email?.trim() ||
      participant.user?.email ||
      participant.exhibitor?.email?.trim() ||
      participant.sponsor?.email?.trim() ||
      "",
    type: participant.entityType,
    company: profile?.company?.trim() || exhibitorName || sponsorName || "",
  };
}
